// SETTINGS V2 — grouped nav hook (SETTINGS_V2_SP0 §6).
//
// Returns 3 groups (Účet / Firma / Servis a obchod) in the PM-approved order
// (Decision #3 GRANTED, §6.1). Keys/paths are IDENTICAL to the legacy nav; only
// team's labelKey differs (settings:nav.usersAndAccess per parent ticket §4.0).
//
// The `visible` field is THE PRESERVED FILTER SEAM — all true today. Future
// gating: pricing/service = isProvider || isHybrid, shipping = hasInventory.
// A group whose items all become invisible drops out entirely (eyebrow + all).
//
// NO iconName string field — glyphs are resolved by `key` from
// settingsV2NavGlyphs in the rail. NO appearance entry, ever. The hook does NOT
// read the router — active matching lives in the rail (§6.2 / §7.2).
import { useMemo } from "react";

import { SETTINGS_BASE } from "./settingsV2Paths";

export interface SettingsV2NavItem {
  key: string; // react key + glyph lookup + slice activeSection value
  labelKey: string; // i18n key, translated by the consumer
  path: string; // NavLink target segment under SETTINGS_BASE (/settings)
  visible: boolean; // THE PRESERVED FILTER SEAM — all true today
}

export interface SettingsV2NavGroup {
  key: "account" | "company" | "serviceCommerce";
  labelKey: string; // settings:nav.group* — translated by the consumer
  items: SettingsV2NavItem[]; // already visible-filtered
}

interface RawGroup {
  key: SettingsV2NavGroup["key"];
  labelKey: string;
  items: SettingsV2NavItem[];
}

/** Exact-or-nested active match, mirroring legacy SettingsSidebar.isActive
 *  (§6.2). Pure — consumers pass location.pathname, so the hook itself still
 *  never reads the router (the SP0 §6.2 rule stays intact). Shared by the rail,
 *  the tablet tab-bar, the phone selector, and the phone sheet so all four nav
 *  surfaces agree on which section is active. */
export const isSettingsV2SectionActive = (
  pathname: string,
  path: string
): boolean =>
  pathname === `${SETTINGS_BASE}/${path}` ||
  pathname.startsWith(`${SETTINGS_BASE}/${path}/`);

const useSettingsV2Nav = (): { groups: SettingsV2NavGroup[] } => {
  const groups = useMemo<SettingsV2NavGroup[]>(() => {
    const raw: RawGroup[] = [
      {
        key: "account",
        labelKey: "settings:nav.groupAccount",
        items: [
          { key: "profile", labelKey: "settings:nav.profile", path: "profile", visible: true },
          { key: "notifications", labelKey: "settings:nav.notifications", path: "notifications", visible: true },
        ],
      },
      {
        key: "company",
        labelKey: "settings:nav.groupCompany",
        items: [
          { key: "company", labelKey: "settings:nav.company", path: "company", visible: true },
          { key: "locations", labelKey: "settings:nav.locations", path: "locations", visible: true },
          // B7-FE1 (plan §6A.4) — the digital-nameplate control panel. Sits in the
          // company group after locations; isSettingsV2SectionActive already
          // matches the nested /:machineId deep link, so no nav-highlight work.
          { key: "nameplates", labelKey: "settings:nav.nameplates", path: "nameplates", visible: true },
          { key: "team", labelKey: "settings:nav.usersAndAccess", path: "team", visible: true },
        ],
      },
      {
        key: "serviceCommerce",
        labelKey: "settings:nav.groupServiceCommerce",
        items: [
          { key: "pricing", labelKey: "settings:nav.pricing", path: "pricing", visible: true }, // Future: isProvider || isHybrid
          { key: "service", labelKey: "settings:nav.service", path: "service", visible: true }, // Future: isProvider || isHybrid
          { key: "shipping", labelKey: "settings:nav.shipping", path: "shipping", visible: true }, // Future: hasInventory
        ],
      },
    ];

    return raw
      .map((g) => ({ ...g, items: g.items.filter((i) => i.visible) }))
      .filter((g) => g.items.length > 0);
  }, []);

  return { groups };
};

export default useSettingsV2Nav;
